import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import LinearProgress from "@material-ui/core/LinearProgress";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";

import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import StatusChip from "../../components/StatusChip";
import { semantic } from "../../theme/tokens";

/**
 * Cada motivo tem um tom próprio: o vendedor precisa distinguir "cobrar
 * proposta" (dinheiro parado na mesa) de "sem data prevista" (higiene de
 * cadastro) num relance.
 */
const ACTION_TONE = {
  sem_contato: "danger",
  proposta_sem_resposta: "warning",
  proposta_pendente: "info",
  sem_proximo_passo: "neutral",
  sem_valor: "neutral",
  sem_data: "neutral",
};

const useStyles = makeStyles((theme) => ({
  tile: { padding: theme.spacing(2), border: `1px solid ${theme.palette.divider}`, height: "100%" },
  tileLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  tileValue: { fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" },
  tileHint: { color: theme.palette.text.secondary },
  section: { marginBottom: theme.spacing(3) },
  card: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitle: { fontWeight: 600, display: "flex", alignItems: "center", gap: theme.spacing(1) },
  cardMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
    marginTop: 2,
  },
  actions: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: theme.spacing(1) },
  side: {
    flex: "none",
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
    [theme.breakpoints.down("xs")]: { alignItems: "flex-start", textAlign: "left" },
  },
  value: { fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "1.05rem" },
  idle: { fontSize: 12, fontVariantNumeric: "tabular-nums" },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  filter: { minWidth: 150 },
}));

const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const DealAttention = () => {
  const classes = useStyles();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [onlyStale, setOnlyStale] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setOwners(r.data.users || [])).catch(() => setOwners([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/analytics/deal-attention", {
        params: { ownerId: ownerId || undefined, onlyStale: onlyStale || undefined },
      });
      setData(result);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [ownerId, onlyStale]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const t = data?.totals;

  return (
    <MainContainer>
      <PageHeader
        title="Precisam de atenção"
        count={data?.deals?.length}
        subtitle={`Negócios abertos com pendência. "Parado" considera a última interação real — mensagem, tarefa, compromisso ou movimentação —, não a última edição do registro.`}
        filters={
          <>
            <TextField
              select
              label="Responsável"
              className={classes.filter}
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {owners.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Mostrar"
              className={classes.filter}
              value={onlyStale ? "stale" : "all"}
              onChange={(e) => setOnlyStale(e.target.value === "stale")}
            >
              <MenuItem value="all">Tudo com pendência</MenuItem>
              <MenuItem value="stale">Só os parados</MenuItem>
            </TextField>
          </>
        }
      />

      {loading && <LinearProgress />}

      <div style={{ paddingTop: 16 }}>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Parados</div>
              <div className={classes.tileValue} style={t?.stale ? { color: semantic.danger.main } : undefined}>
                {t?.stale ?? "—"}
              </div>
              <Typography variant="caption" className={classes.tileHint}>
                {data ? `${money(t.staleValue)} sem interação há ${data.staleDays}+ dias` : ""}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Propostas aguardando</div>
              <div className={classes.tileValue} style={t?.proposalsWaiting ? { color: semantic.warning.main } : undefined}>
                {t?.proposalsWaiting ?? "—"}
              </div>
              <Typography variant="caption" className={classes.tileHint}>
                Enviadas e sem resposta
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Sem próximo passo</div>
              <div className={classes.tileValue}>{t?.withoutNextStep ?? "—"}</div>
              <Typography variant="caption" className={classes.tileHint}>
                Nenhuma tarefa aberta
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Negócios abertos</div>
              <div className={classes.tileValue}>{t?.openDeals ?? "—"}</div>
              <Typography variant="caption" className={classes.tileHint}>
                No total
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {!loading && !data?.deals?.length && (
          <Paper variant="outlined">
            <div className={classes.empty}>
              <CheckCircleOutlineIcon style={{ fontSize: 40, color: semantic.success.main }} />
              <Typography variant="subtitle1" color="textPrimary">
                Nada pendente por aqui
              </Typography>
              <Typography variant="body2">
                Todos os negócios abertos têm próximo passo e interação recente.
              </Typography>
            </div>
          </Paper>
        )}

        {(data?.deals || []).map((d) => (
          <Paper variant="outlined" key={d.dealId} className={classes.card}>
            <div className={classes.cardBody}>
              <div className={classes.cardTitle}>
                {d.title}
                <Tooltip title="Abrir no pipeline">
                  <Button size="small" onClick={() => history.push("/deals")}>
                    <OpenInNewIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </div>
              <div className={classes.cardMeta}>
                {d.clientName || "sem cliente"}
                {d.stageName ? ` · ${d.stageName} (${d.stageProbability}%)` : ""}
                {d.ownerName ? ` · ${d.ownerName}` : ""}
              </div>
              <div className={classes.actions}>
                {d.actions.map((a) => (
                  <StatusChip key={a.key} dot tone={ACTION_TONE[a.key] || "neutral"} label={a.label} />
                ))}
              </div>
            </div>
            <div className={classes.side}>
              <span className={classes.value}>{money(d.value)}</span>
              <span
                className={classes.idle}
                style={
                  d.daysIdle >= (data?.staleDays || 7)
                    ? { color: semantic.danger.main, fontWeight: 600 }
                    : { color: "inherit", opacity: 0.7 }
                }
              >
                {d.daysIdle === 0 ? "interação hoje" : `${d.daysIdle} dias parado`}
              </span>
            </div>
          </Paper>
        ))}
      </div>
    </MainContainer>
  );
};

export default DealAttention;
